import { Dispatch, SetStateAction, useRef, useState } from "react"
import useSWRImmutable from "swr/immutable"

import {
    getEmptyHpDeviceFormData,
    mapHpDeviceResponseToDeviceFormData,
} from "../../../../../components/devices/lct/formSpecs/hpSpec"
import { HpDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import { getClientProtected } from "../../../clients/clients"
import { CONTINUE_DRAFT_CONFIG } from "../../../swrConfig"
import { ContinueHpDraftResponse } from "../../../types/devices/lct/publishHpDeviceTypes"
import { CONTINUE_LCT_DEVICE_DRAFT } from "../../../urls/private"

export interface IUseContinueHpDeviceDraftOutput {
    data: HpDeviceFormData
    setData: Dispatch<SetStateAction<HpDeviceFormData>>
    isLoading: boolean
    isValidating: boolean
    attachmentUUID: string | null
    apiResSystemRef: string | null
    error?: AppError
}

const continueHpDeviceDraftMapper = getTsIoMapper(
    ContinueHpDraftResponse,
    "continueHpDeviceDraftMapper"
)

const useContinueHpDeviceDraft = (
    deviceRef: string,
    dataset: LCTOnlyDatasets = LCTOnlyDatasets.HP
): IUseContinueHpDeviceDraftOutput => {
    const [data, setData] = useState<HpDeviceFormData>(
        getEmptyHpDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    // Only to be set in the SWR fetcher. Ref used as useState triggers the
    // re-render
    const attachmentUUID = useRef<string | null>(null)
    const apiResSystemRef = useRef<string | null>(null)

    const { error, isValidating } = useSWRImmutable(
        CONTINUE_LCT_DEVICE_DRAFT(deviceRef, dataset),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes = continueHpDeviceDraftMapper.map(response)
                attachmentUUID.current = mappedRes.Attachment_UUID
                apiResSystemRef.current = mappedRes.SystemReference
                setData(mapHpDeviceResponseToDeviceFormData(mappedRes))
            } catch {
                throw invalidApiDataError("Failed to continue draft version")
            } finally {
                setIsLoading(false)
            }
        },
        CONTINUE_DRAFT_CONFIG
    )

    return {
        data,
        setData,
        isLoading,
        isValidating,
        attachmentUUID: attachmentUUID.current,
        apiResSystemRef: apiResSystemRef.current,
        error,
    }
}

export default useContinueHpDeviceDraft
